// Iter187 · TenderIntelligence
// -----------------------------------------------------------------------------
// Módulo de inteligencia comercial inspirado en elicita.es. Reúne en una sola
// pantalla:
//   1. KPIs de mercado (clientes, licitaciones vigentes, vencimientos, top
//      competidores).
//   2. Grafo interactivo Clientes ↔ Licitaciones ↔ Competidores
//      (react-force-graph-2d).
//   3. Tarjetas de renovación estimada con contador regresivo y confianza.
//   4. Panel top competidores por cuota adjudicada.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import ForceGraph2D from "react-force-graph-2d";
import { InsightsPanel } from "./tender_intelligence/InsightsPanel";
import { ProvinceHeatMap } from "./tender_intelligence/ProvinceHeatMap";
import { WasteTreatmentPanel } from "./tender_intelligence/WasteTreatmentPanel";
import { fmtFechaOferta } from "./ofertas_comercial/shared";
import {
  Network, Users, Timer, Trophy, Search, Filter, X, ExternalLink,
  RefreshCw, Loader2, Building2, ChevronRight, Mail, AlertTriangle, MapPin,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtM = (n) => n == null ? "—" : `${(Number(n) / 1_000_000).toFixed(2)} M€`;
const NODE_COLOR = {
  client:     "#2563eb",  // azul (por defecto)
  tender:     "#059669",  // verde
  competitor: "#dc2626",  // rojo
};

// Iter256 Mensaje 1: persistencia de capas del grafo (Cartera ON / Mercado OFF)
const LAYERS_LS_KEY = "ti_graph_layers_v1";
function loadLayerPrefs() {
  try {
    const raw = localStorage.getItem(LAYERS_LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* noop */ }
  return { cartera: true, mercado: false, includeNonStrategic: false };
}

// Color de un cliente según win_rate VSM (rojo = perdemos, verde = ganamos).
// Iter256 Mensaje 2: con muestra insuficiente (n<3) no pintamos semáforo.
function clientColor(node) {
  const wr = node.win_rate_pct;
  if (wr == null || node.low_confidence) return "#2563eb"; // sin datos fiables → azul
  if (wr >= 70)   return "#059669";       // verde: dominamos
  if (wr >= 40)   return "#f59e0b";       // ámbar: territorio disputado
  return "#dc2626";                        // rojo: perdemos
}

// Tamaño del nodo (react-force-graph 2d usa area = nodeVal). Escalamos por
// log10(importe) para que licitaciones grandes se destaquen sin colapsar.
function nodeSize(n, mode) {
  if (n.type === "tender") {
    return Math.max(3, Math.log10((n.importe || 0) + 1) * 1.6);
  }
  if (n.type === "client") {
    const total = n.total_importe || 0;
    const base = Math.log10(total + 1) * (mode === "by_client" ? 1.8 : 1.4);
    return Math.max(4, base);
  }
  // competitor
  const total = n.total_importe || 0;
  return Math.max(3, Math.log10(total + 1) * (mode === "by_client" ? 1.6 : 1.2));
}

export default function TenderIntelligence() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [graph, setGraph] = useState({ nodes: [], links: [], counts: {} });
  const [renewals, setRenewals] = useState({ items: [], total: 0 });
  const [competitors, setCompetitors] = useState({ top: [] });
  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState(false);
  const [dg, setDg] = useState("all");
  const [graphDg, setGraphDg] = useState("all"); // filtro DG LOCAL del grafo
  const [graphProvincia, setGraphProvincia] = useState(""); // Iter236 · filtro provincia LOCAL del grafo (desde el mapa)
  const [graphProvinciaLabel, setGraphProvinciaLabel] = useState("");
  const [graphMode, setGraphMode] = useState("detailed"); // detailed | by_client
  const [activeTab, setActiveTab] = useState("grafo"); // grafo | insights
  const [layerPrefs, setLayerPrefs] = useState(loadLayerPrefs); // Iter256 Mensaje 1
  const [q, setQ] = useState("");
  const [maxNodes, setMaxNodes] = useState(120);
  const [selectedNode, setSelectedNode] = useState(null);
  const [clientDetail, setClientDetail] = useState(null); // Cliente 360º
  const [clientLoading, setClientLoading] = useState(false);
  const [bucketFilter, setBucketFilter] = useState("");
  const [minMm, setMinMm] = useState(0); // Iter282 · filtro de importe (M€) persistente
  const [sendingAlert, setSendingAlert] = useState(false);
  const [rivalAlerts, setRivalAlerts] = useState([]); // Iter260 · Alertas de Rivales
  const graphRef = useRef();

  useEffect(() => {
    try { localStorage.setItem(LAYERS_LS_KEY, JSON.stringify(layerPrefs)); } catch { /* noop */ }
  }, [layerPrefs]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (mine) p.set("mine", "1");
    if (dg && dg !== "all") p.set("dg", dg);
    return p.toString();
  }, [mine, dg]);

  const graphQs = useMemo(() => {
    const p = new URLSearchParams();
    if (mine) p.set("mine", "1");
    // El grafo permite un DG independiente del global
    const g = graphDg !== "all" ? graphDg : (dg !== "all" ? dg : null);
    if (g) p.set("dg", g);
    if (graphProvincia) p.set("provincia", graphProvincia);
    p.set("cartera", layerPrefs.cartera ? "1" : "0");
    p.set("mercado", layerPrefs.mercado ? "1" : "0");
    if (layerPrefs.mercado && layerPrefs.includeNonStrategic) p.set("include_non_strategic", "1");
    return p.toString();
  }, [mine, dg, graphDg, graphProvincia, layerPrefs]);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4, r5] = await Promise.all([
        axios.get(`${API}/tender-intelligence/kpis${qs ? `?${qs}` : ""}`),
        axios.get(`${API}/tender-intelligence/graph?max_nodes=${maxNodes}&mode=${graphMode}${graphQs ? `&${graphQs}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
        axios.get(`${API}/tender-intelligence/renewals?limit=60${qs ? `&${qs}` : ""}${minMm ? `&min_mm=${minMm}` : ""}`),
        axios.get(`${API}/tender-intelligence/competitors?limit=10`),
        axios.get(`${API}/tender-intelligence/alerts/rivales`),
      ]);
      setKpis(r1.data);
      setGraph(r2.data);
      setRenewals(r3.data);
      setCompetitors(r4.data);
      setRivalAlerts(r5.data.items || []);
    } catch (e) {
      toast.error("Error al cargar Tender Intelligence");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [qs, maxNodes, minMm]);

  const rebuildGraph = () => {
    axios.get(`${API}/tender-intelligence/graph?max_nodes=${maxNodes}&mode=${graphMode}${graphQs ? `&${graphQs}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`)
      .then(r => setGraph(r.data));
  };

  // Refrescar sólo el grafo cuando cambia graphDg, graphMode, provincia o las capas
  useEffect(() => { rebuildGraph(); /* eslint-disable-next-line */ }, [graphDg, graphProvincia, graphMode, layerPrefs]);

  // Cargar detalle 360º cuando se selecciona un cliente
  useEffect(() => {
    if (selectedNode?.type !== "client") {
      setClientDetail(null);
      return;
    }
    setClientLoading(true);
    setClientDetail(null);
    axios.get(`${API}/tender-intelligence/client?name=${encodeURIComponent(selectedNode.label)}`)
      .then(r => setClientDetail(r.data))
      .catch(() => toast.error("No se pudo cargar el detalle del cliente"))
      .finally(() => setClientLoading(false));
  }, [selectedNode]);

  const sendAlertNow = async () => {
    setSendingAlert(true);
    try {
      const { data } = await axios.post(`${API}/tender-intelligence/alerts/send-now`);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Digest enviado · ${data.sent}/${data.recipients} destinatarios (${data.cards} contratos ≤90d)`);
      }
    } catch (e) {
      toast.error("Error enviando el digest de renovaciones");
    } finally { setSendingAlert(false); }
  };

  const filteredRenewals = bucketFilter
    ? renewals.items.filter(c => c.bucket === bucketFilter)
    : renewals.items;

  const ackRivalAlert = async (id) => {
    setRivalAlerts(prev => prev.filter(a => a.id !== id)); // optimista
    try {
      await axios.post(`${API}/tender-intelligence/alerts/rivales/${id}/ack`);
    } catch (e) {
      toast.error("No se pudo descartar la alerta");
      load();
    }
  };

  // Iter236 · click en una provincia del mapa → filtra el grafo por esa provincia
  const filterGraphByProvince = (rawValuesCsv, provinciaLabel) => {
    setGraphProvincia(rawValuesCsv);
    setGraphProvinciaLabel(provinciaLabel);
    setActiveTab("grafo");
  };

  return (
    <div className="p-6 space-y-5 bg-gradient-to-br from-slate-50 via-white to-blue-50 min-h-screen" data-testid="tender-intelligence-view">
      {/* Header + Toolbar */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-600 text-white flex items-center justify-center shadow-sm">
            <Network size={20}/>
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Tender Intelligence</h1>
            <p className="text-xs text-slate-500">Grafo Clientes ↔ Licitaciones ↔ Competidores · renovaciones · cuota de mercado</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[11px] flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={mine} onChange={e => setMine(e.target.checked)} data-testid="ti-mine-toggle"/>
            <span>Solo mías</span>
          </label>
          <select value={dg} onChange={e => setDg(e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1 bg-white" data-testid="ti-dg-select">
            <option value="all">Todas DG</option>
            <option value="SERVICIOS">Servicios Urbanos</option>
            <option value="TRATAMIENTO">Tratamiento</option>
          </select>
          {/* Iter282 · Importe ≥ X M€ — aplica al listado de renovaciones */}
          <div
            data-testid="ti-filter-importe-wrap"
            title="Filtra renovaciones con importe por encima del umbral"
            className={`inline-flex items-center gap-1.5 border rounded-lg px-2 py-1 text-[11px] ${
              minMm > 0 ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-white"
            }`}>
            <span className={minMm > 0 ? "font-semibold text-sky-700" : "text-slate-500"}>Importe ≥</span>
            <input
              type="range" min={0} max={30} step={1}
              value={minMm}
              onChange={(e) => setMinMm(Number(e.target.value))}
              data-testid="ti-filter-importe"
              className="accent-sky-500 w-20"
            />
            <span className={`font-mono w-12 text-right ${minMm > 0 ? "text-sky-700 font-bold" : "text-slate-500"}`}>{minMm}M€</span>
          </div>
          <button onClick={sendAlertNow} disabled={sendingAlert}
            className="text-xs px-3 py-1 rounded bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-1"
            title="Enviar ahora el digest semanal de renovaciones ≤ 90d a admins + offer managers"
            data-testid="ti-send-alert-now">
            {sendingAlert ? <Loader2 size={13} className="animate-spin"/> : <Mail size={12}/>} Enviar digest
          </button>
          <button onClick={load} className="text-xs px-3 py-1 rounded bg-indigo-600 text-white font-semibold hover:bg-indigo-700 flex items-center gap-1" data-testid="ti-reload">
            {loading ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={12}/>} Actualizar
          </button>
        </div>
      </header>

      {/* Iter260 · Banner Alertas de Rivales — rival nuevo (real, no basura) que
          nos gana en un cliente donde antes siempre ganábamos. Revisión diaria
          automática (07:50 UTC) + digest por email; aquí solo se muestra/descarta. */}
      {rivalAlerts.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 space-y-2" data-testid="ti-rival-alerts-banner">
          <div className="flex items-center gap-2 text-rose-700 font-bold text-xs">
            <AlertTriangle size={14}/> Rival nuevo detectado en {rivalAlerts.length} cliente{rivalAlerts.length > 1 ? "s" : ""} donde siempre ganábamos
          </div>
          <div className="grid gap-1.5 md:grid-cols-2">
            {rivalAlerts.map(a => (
              <div key={a.id} className="flex items-start justify-between gap-2 bg-white border border-rose-100 rounded-lg px-3 py-2 text-[11px]" data-testid={`ti-rival-alert-${a.id}`}>
                <div>
                  <div className="font-semibold text-slate-800">{a.cliente} <span className="text-rose-600">· {a.competidor}</span></div>
                  <div className="text-slate-500 truncate max-w-[280px]">{a.objeto_contrato}</div>
                  <div className="text-slate-400">Ganábamos {a.n_previas_ganadas}/{a.n_previas_ganadas} anteriores · Exp: {a.expediente || "—"}</div>
                </div>
                <button onClick={() => ackRivalAlert(a.id)} data-testid={`ti-rival-alert-ack-${a.id}`}
                        className="shrink-0 text-[10px] font-semibold px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200">
                  Descartar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Iter259 (Mensaje 3) · Pestañas Grafo / Insights / Mapa / Waste Treatment */}
      <div className="flex gap-2 flex-wrap" data-testid="ti-main-tabs">
        {[
          { key: "grafo", label: "Grafo" },
          { key: "insights", label: "Insights" },
          { key: "mapa", label: "Mapa por provincia" },
          { key: "tratamiento", label: "Waste Treatment" },
        ].map(tb => (
          <button key={tb.key} onClick={() => setActiveTab(tb.key)}
            className={`text-xs font-semibold px-4 py-2 rounded-lg border transition-all ${activeTab === tb.key ? "bg-[#007934] text-white border-[#007934] shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
            data-testid={`ti-tab-${tb.key}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {activeTab === "insights" && (
        <InsightsPanel onFilterClient={(cliente) => { setActiveTab("grafo"); setQ(cliente); }} />
      )}

      {activeTab === "mapa" && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4" data-testid="ti-mapa-tab">
          <ProvinceHeatMap onSelectProvince={filterGraphByProvince} />
        </div>
      )}

      {activeTab === "tratamiento" && (
        <WasteTreatmentPanel onFilterGraph={filterGraphByProvince} />
      )}

      {activeTab === "grafo" && (<>
      {/* KPIs superiores */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="ti-kpis">
          <KPICard label="Clientes activos" value={kpis.clientes_activos} icon={<Building2 size={14}/>} color="from-blue-500 to-indigo-600" testid="kpi-clientes"/>
          <KPICard label="Licitaciones vigentes" value={kpis.licitaciones_vigentes} icon={<Timer size={14}/>} color="from-emerald-500 to-teal-600" testid="kpi-vigentes"/>
          <KPICard label="Vencen ≤ 30 días" value={kpis.vencen_30} icon={<Timer size={14}/>} color="from-rose-500 to-red-600" testid="kpi-vencen-30" onClick={() => setBucketFilter(bucketFilter === "30" ? "" : "30")} active={bucketFilter==="30"}/>
          <KPICard label="Vencen 30-60d" value={kpis.vencen_60} icon={<Timer size={14}/>} color="from-orange-500 to-amber-600" testid="kpi-vencen-60" onClick={() => setBucketFilter(bucketFilter === "60" ? "" : "60")} active={bucketFilter==="60"}/>
          <KPICard label="Vencen 60-90d" value={kpis.vencen_90} icon={<Timer size={14}/>} color="from-amber-500 to-yellow-600" testid="kpi-vencen-90" onClick={() => setBucketFilter(bucketFilter === "90" ? "" : "90")} active={bucketFilter==="90"}/>
          <KPICard label="Total addressable" value={fmtM(kpis.addressable_importe)} icon={<Trophy size={14}/>} color="from-purple-500 to-pink-600" testid="kpi-addressable"/>
        </div>
      )}

      {/* Grafo + panel derecho */}
      <section className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="ti-graph-container">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 bg-slate-50">
            <Network size={14} className="text-indigo-600"/>
            <h3 className="text-sm font-semibold text-slate-800">Grafo del universo</h3>
            <span className="text-[11px] text-slate-500" title="El grafo incluye TODO el histórico (sin filtro de fecha). Los KPIs de arriba muestran solo licitaciones vigentes (fecha_presentación futura) — por eso los totales no coinciden.">
              {graph.counts?.clients || 0} clientes · {graph.counts?.tenders || 0} licitaciones · {graph.counts?.competitors || 0} competidores
              <span className="ml-1 text-slate-400">(histórico completo, no solo vigentes)</span>
            </span>
            <div className="ml-auto flex items-center gap-2">
              <div className="flex items-center rounded overflow-hidden border border-slate-200" data-testid="ti-graph-mode-toggle">
                {[
                  { k: "detailed", label: "Detalle" },
                  { k: "by_client", label: "Por cliente" },
                ].map(m => (
                  <button
                    key={m.k}
                    onClick={() => setGraphMode(m.k)}
                    className={`text-[11px] px-2.5 py-1 font-semibold transition ${graphMode === m.k ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                    data-testid={`ti-graph-mode-${m.k}`}
                  >{m.label}</button>
                ))}
              </div>
              <select value={graphDg} onChange={e => setGraphDg(e.target.value)}
                className="text-[11px] border border-slate-200 rounded px-2 py-1 bg-white"
                title="Filtro DG SÓLO para el grafo (no afecta a KPIs ni renovaciones)"
                data-testid="ti-graph-dg-select">
                <option value="all">Grafo · Todas DG</option>
                <option value="SERVICIOS">Grafo · Servicios</option>
                <option value="TRATAMIENTO">Grafo · Tratamiento</option>
                <option value="OTRO">Grafo · Otro</option>
              </select>
              {graphProvincia && (
                <button
                  onClick={() => { setGraphProvincia(""); setGraphProvinciaLabel(""); }}
                  className="text-[11px] px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200 flex items-center gap-1"
                  data-testid="ti-graph-provincia-filter-badge"
                  title="Filtro de provincia aplicado desde el Mapa — clic para quitarlo"
                >
                  <MapPin size={10}/> {graphProvinciaLabel || "Provincia"} <X size={10}/>
                </button>
              )}
              <div className="relative">
                <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input
                  type="text"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") rebuildGraph(); }}
                  placeholder="Buscar organismo, expediente, objeto..."
                  className="text-[11px] pl-6 pr-2 py-1 border border-slate-200 rounded w-56"
                  data-testid="ti-graph-search"
                />
              </div>
              <select value={maxNodes} onChange={e => setMaxNodes(Number(e.target.value))} className="text-[11px] border border-slate-200 rounded px-2 py-1 bg-white" data-testid="ti-max-nodes">
                <option value={60}>60 nodos</option>
                <option value={120}>120 nodos</option>
                <option value={200}>200 nodos</option>
                <option value={400}>400 nodos</option>
              </select>
              <button onClick={rebuildGraph} className="text-[11px] px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200" data-testid="ti-graph-refresh">Aplicar</button>
            </div>
          </div>
          {/* Iter256 Mensaje 1 · toggle de capas Cartera / Mercado */}
          <div className="flex items-center gap-3 border-b border-slate-100 px-3 py-1.5 bg-white" data-testid="ti-layers-toolbar">
            <span className="text-[10px] font-bold uppercase text-slate-400">Capas</span>
            <label className="text-[11px] flex items-center gap-1.5 cursor-pointer" title="Ofertas comerciales reales (o:) — cartera real de VSM">
              <input type="checkbox" checked={layerPrefs.cartera}
                onChange={e => setLayerPrefs(p => ({ ...p, cartera: e.target.checked }))}
                data-testid="ti-layer-cartera-toggle"/>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Cartera</span>
            </label>
            <label className="text-[11px] flex items-center gap-1.5 cursor-pointer" title="Oportunidades del scraper PLACSP (t:), filtradas por QA — NUNCA se mezclan con la cartera real">
              <input type="checkbox" checked={layerPrefs.mercado}
                onChange={e => setLayerPrefs(p => ({ ...p, mercado: e.target.checked }))}
                data-testid="ti-layer-mercado-toggle"/>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Mercado (QA)</span>
            </label>
            {layerPrefs.mercado && (
              <label className="text-[11px] flex items-center gap-1.5 cursor-pointer text-slate-500">
                <input type="checkbox" checked={layerPrefs.includeNonStrategic}
                  onChange={e => setLayerPrefs(p => ({ ...p, includeNonStrategic: e.target.checked }))}
                  data-testid="ti-layer-include-non-strategic-toggle"/>
                Incluir no estratégicos
              </label>
            )}
            {!layerPrefs.mercado && (
              <span className="text-[10px] text-slate-400 italic">Mercado oculto por defecto — activa para ver oportunidades del scraper ya filtradas por calidad.</span>
            )}
          </div>
          <div className="bg-slate-50" style={{ height: 520 }}>
            {loading && graph.nodes.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400"><Loader2 size={22} className="animate-spin"/></div>
            ) : (
              <ForceGraph2D
                ref={graphRef}
                graphData={graph}
                nodeLabel={(n) => {
                  if (n.type === "tender") {
                    const badge = n.layer === "mercado" ? "🔎 Mercado (QA)" : "💼 Oferta";
                    return `📄 ${badge} · ${n.label}\n${fmtM(n.importe)} · ${n.estado || "—"}`;
                  }
                  if (n.type === "client") {
                    const total = fmtM(n.total_importe || 0);
                    const wr = n.win_rate_pct != null ? `${n.win_rate_pct}%` : "—";
                    const nInfo = n.low_confidence ? ` (n=${n.n_played || 0}, baja confianza)` : "";
                    const tc = n.top_competitor ? `Top comp: ${n.top_competitor.name} (${fmtM(n.top_competitor.importe)})` : "";
                    return `🏛 ${n.label}\n${total} · ${n.n_tenders || 0} licit. · win rate ${wr}${nInfo}${tc ? "\n" + tc : ""}`;
                  }
                  // competitor
                  return `🏢 ${n.label}\n${fmtM(n.total_importe || 0)} · ${n.n_wins || 0} adj. · clientes: ${(n.clientes || []).slice(0,3).join(", ")}`;
                }}
                nodeAutoColorBy="type"
                nodeVal={(n) => nodeSize(n, graph.mode || graphMode)}
                nodeColor={(n) => n.type === "client" ? clientColor(n) : (NODE_COLOR[n.type] || "#94a3b8")}
                linkColor={(l) => l.kind === "won" ? "rgba(220,38,38,0.35)" : (l.kind === "incumbent" ? "rgba(202,138,4,0.35)" : (l.kind === "aggregate" ? "rgba(37,99,235,0.45)" : "rgba(37,99,235,0.25)"))}
                linkWidth={(l) => Math.max(0.5, Math.log10((l.value || 0) + 1) * (l.kind === "aggregate" ? 0.7 : 0.4))}
                onNodeClick={(n) => setSelectedNode(n)}
                cooldownTicks={80}
                width={undefined}
                height={520}
              />
            )}
          </div>
        </div>

        {/* Panel derecho detalle */}
        <aside className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 space-y-3" data-testid="ti-detail-panel">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1"><Filter size={13}/> Detalle</h3>
          {!selectedNode && (
            <div className="text-xs text-slate-500 italic py-6 text-center">
              Haz clic en un nodo del grafo para ver su detalle aquí.
            </div>
          )}
          {selectedNode && (
            <div className="space-y-2 text-xs" data-testid="ti-selected-detail">
              <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ backgroundColor: `${NODE_COLOR[selectedNode.type]}22`, color: NODE_COLOR[selectedNode.type] }}>
                {selectedNode.type === "client" ? "Cliente" : selectedNode.type === "tender" ? "Licitación" : "Competidor"}
              </div>
              <div className="font-bold text-slate-800 leading-snug">{selectedNode.label}</div>
              {selectedNode.type === "tender" && (
                <>
                  <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${selectedNode.layer === "mercado" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="ti-tender-layer-badge">
                    {selectedNode.layer === "mercado" ? "Mercado (QA)" : "Oferta"}
                  </span>
                  <div className="text-slate-500">Estado: <b>{selectedNode.estado || "—"}</b></div>
                  <div className="text-slate-500">DG: <b>{selectedNode.dg || "—"}</b></div>
                  <div className="text-slate-500">Provincia: <b>{selectedNode.provincia || "—"}</b></div>
                  <div className="text-slate-500">Presenta: <b>{fmtFechaOferta(selectedNode.fecha_presentacion)}</b></div>
                  <div className="text-slate-500">Importe: <b className="font-mono">{fmtM(selectedNode.importe)}</b></div>
                  {selectedNode.expediente && <div className="text-slate-500">Exp: <b className="font-mono">{selectedNode.expediente}</b></div>}
                  <a href={`/ofertas-comercial?tab=mercado&highlight=${selectedNode.mercado_id}`} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline mt-1">
                    <ExternalLink size={11}/> Abrir en Mercado
                  </a>
                </>
              )}
              {selectedNode.type === "client" && (
                <>
                  <div className="text-slate-500">Provincia: <b>{selectedNode.provincia || "—"}</b></div>
                  <div className="text-slate-500">DG: <b>{selectedNode.dg || "—"}</b></div>
                  {selectedNode.total_importe != null && (
                    <div className="rounded bg-slate-50 p-2 space-y-0.5 text-[11px]">
                      <div>Importe total en juego: <b className="font-mono">{fmtM(selectedNode.total_importe)}</b></div>
                      <div>Licitaciones detectadas: <b>{selectedNode.n_tenders || 0}</b> · Ganadas VSM: <b className="text-emerald-700">{selectedNode.n_won_vsm || 0}</b> · Perdidas: <b className="text-rose-700">{selectedNode.n_lost || 0}</b></div>
                      {selectedNode.win_rate_pct != null && !selectedNode.low_confidence && (
                        <div>Win rate VSM: <b className={selectedNode.win_rate_pct >= 70 ? "text-emerald-700" : selectedNode.win_rate_pct >= 40 ? "text-amber-700" : "text-rose-700"}>{selectedNode.win_rate_pct}%</b> <span className="text-slate-400">(n={selectedNode.n_played})</span></div>
                      )}
                      {selectedNode.win_rate_pct != null && selectedNode.low_confidence && (
                        <div className="text-slate-500" data-testid="ti-client-low-confidence">n={selectedNode.n_played}, muestra insuficiente para tasa fiable</div>
                      )}
                      {selectedNode.n_played === 0 && (
                        <div className="text-slate-400 italic">Sin adjudicaciones cerradas todavía</div>
                      )}
                      {selectedNode.top_competitor && (
                        <div>Top competidor: <b>{selectedNode.top_competitor.name}</b> <span className="font-mono text-slate-400">({fmtM(selectedNode.top_competitor.importe)})</span></div>
                      )}
                    </div>
                  )}
                  {clientLoading && (
                    <div className="flex items-center gap-1.5 text-slate-500 pt-2"><Loader2 size={11} className="animate-spin"/> Cargando 360º…</div>
                  )}
                  {clientDetail && (
                    <div className="mt-2 space-y-2" data-testid="ti-client-360">
                      {/* Resumen 360 */}
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="rounded bg-indigo-50 p-1.5 text-center">
                          <div className="text-[9px] text-indigo-700 font-bold uppercase">Mercado</div>
                          <div className="text-sm font-bold text-slate-800">{clientDetail.summary?.mercado_count ?? 0}</div>
                        </div>
                        <div className="rounded bg-emerald-50 p-1.5 text-center">
                          <div className="text-[9px] text-emerald-700 font-bold uppercase">Ofertas</div>
                          <div className="text-sm font-bold text-slate-800">{clientDetail.summary?.ofertas_count ?? 0}</div>
                        </div>
                        <div className="rounded bg-amber-50 p-1.5 text-center">
                          <div className="text-[9px] text-amber-700 font-bold uppercase">Ganadas VSM</div>
                          <div className="text-sm font-bold text-slate-800">{clientDetail.summary?.ganadas_vsm ?? 0}</div>
                        </div>
                        <div className="rounded bg-rose-50 p-1.5 text-center">
                          <div className="text-[9px] text-rose-700 font-bold uppercase">Perdidas</div>
                          <div className="text-sm font-bold text-slate-800">{clientDetail.summary?.perdidas ?? 0}</div>
                        </div>
                      </div>
                      {clientDetail.summary?.win_rate_pct != null && !clientDetail.summary?.low_confidence && (
                        <div className="text-[11px] text-slate-600">
                          <b>Win rate VSM:</b>{" "}
                          <span className={clientDetail.summary.win_rate_pct >= 50 ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"}>
                            {clientDetail.summary.win_rate_pct}%
                          </span>
                          {" "}<span className="text-slate-400">(n={clientDetail.summary.n_played})</span>
                          {" · "}
                          <b>Importe hist.:</b> <span className="font-mono">{fmtM(clientDetail.summary.importe_total)}</span>
                        </div>
                      )}
                      {clientDetail.summary?.low_confidence && clientDetail.summary?.n_played > 0 && (
                        <div className="text-[11px] text-slate-500" data-testid="ti-client360-low-confidence">
                          n={clientDetail.summary.n_played}, muestra insuficiente para tasa fiable
                          {" · "}<b>Importe hist.:</b> <span className="font-mono">{fmtM(clientDetail.summary.importe_total)}</span>
                        </div>
                      )}
                      {clientDetail.summary?.renewals_lt_90d > 0 && (
                        <div className="rounded bg-orange-50 border-l-2 border-orange-400 px-2 py-1 text-[11px]">
                          <b className="text-orange-800">{clientDetail.summary.renewals_lt_90d}</b> renovaciones ≤ 90d
                        </div>
                      )}

                      {/* Renovaciones estimadas */}
                      {clientDetail.renewals?.length > 0 && (
                        <div className="mt-1">
                          <div className="text-[10px] font-bold uppercase text-slate-500 mt-1 mb-1">Próximas renovaciones</div>
                          <div className="max-h-40 overflow-auto space-y-1">
                            {clientDetail.renewals.slice(0, 6).map(r => (
                              <div key={r.id} className="text-[11px] border border-slate-100 rounded p-1.5 bg-white">
                                <div className="font-semibold truncate">{r.objeto || "—"}</div>
                                <div className="text-slate-500 flex gap-2 text-[10px]">
                                  <span>{r.proxima_licitacion}</span>
                                  <span className={r.dias_restantes < 0 ? "text-rose-700 font-bold" : r.dias_restantes <= 30 ? "text-rose-600 font-bold" : r.dias_restantes <= 90 ? "text-orange-600 font-semibold" : "text-slate-500"}>
                                    {r.dias_restantes < 0 ? `hace ${Math.abs(r.dias_restantes)}d` : `en ${r.dias_restantes}d`}
                                  </span>
                                  <span className="text-slate-400">{r.confidence}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Competidores que han ganado */}
                      {clientDetail.competitors_stats?.length > 0 && (
                        <div className="mt-1">
                          <div className="text-[10px] font-bold uppercase text-slate-500 mt-1 mb-1">Competidores adjudicatarios</div>
                          <div className="space-y-1">
                            {clientDetail.competitors_stats.slice(0, 5).map((c, i) => (
                              <div key={i} className="text-[11px] flex items-center justify-between border border-slate-100 rounded px-2 py-1 bg-white">
                                <span className="truncate font-semibold text-slate-700">{c.name}</span>
                                <span className="font-mono text-slate-500 shrink-0">{c.wins} · {fmtM(c.importe)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Últimas licitaciones */}
                      {clientDetail.mercado?.length > 0 && (
                        <div className="mt-1">
                          <div className="text-[10px] font-bold uppercase text-slate-500 mt-1 mb-1">Últimas licitaciones (mercado)</div>
                          <div className="max-h-32 overflow-auto space-y-1">
                            {clientDetail.mercado.slice(0, 5).map(m => (
                              <div key={m.id} className="text-[11px] border border-slate-100 rounded p-1.5 bg-white">
                                <div className="truncate font-semibold text-slate-700">{m.titulo_ia || m.objeto_contrato || "(sin título)"}</div>
                                <div className="text-slate-500 text-[10px]">
                                  {fmtFechaOferta(m.fecha_presentacion)} · {m.estado_licitacion || "—"} · {m.presupuesto ? fmtM(m.presupuesto) : "—"}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              {selectedNode.type === "competitor" && (
                <>
                  {selectedNode.total_importe != null && (
                    <div className="text-slate-500">Importe total: <b className="font-mono">{fmtM(selectedNode.total_importe)}</b></div>
                  )}
                  {selectedNode.n_wins != null && (
                    <div className="text-slate-500">Adjudicaciones: <b>{selectedNode.n_wins}</b></div>
                  )}
                  {selectedNode.clientes?.length > 0 && (
                    <div className="text-slate-500 text-[11px] mt-1">
                      <b>Clientes ganados:</b><br/>
                      {selectedNode.clientes.slice(0, 8).join(", ")}{selectedNode.clientes.length > 8 ? "…" : ""}
                    </div>
                  )}
                  <div className="text-[11px] text-slate-500 mt-1">
                    Aristas: {graph.links.filter(l => (l.target?.id || l.target) === selectedNode.id).length} adjudicaciones detectadas
                  </div>
                </>
              )}
              <button onClick={() => setSelectedNode(null)} className="text-[10px] text-slate-400 hover:text-rose-600 flex items-center gap-1"><X size={10}/> Cerrar detalle</button>
            </div>
          )}
          {/* Leyenda */}
          <div className="border-t border-slate-100 pt-2 text-[10px] text-slate-500 space-y-1">
            <div className="font-bold text-slate-600 mb-1">Tamaño de bola = importe total en juego</div>
            <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full" style={{background:"#059669"}}/> Cliente · win rate ≥ 70%</div>
            <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full" style={{background:"#f59e0b"}}/> Cliente · disputado 40-70%</div>
            <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full" style={{background:"#dc2626"}}/> Cliente · perdemos &lt; 40%</div>
            <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full" style={{background:"#2563eb"}}/> Cliente · sin adjudicación aún</div>
            <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full" style={{background:NODE_COLOR.tender}}/> Licitación (modo Detalle)</div>
            <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full" style={{background:NODE_COLOR.competitor}}/> Competidor · winner</div>
            <div className="pt-1 border-t border-slate-100 mt-1">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold mr-1">Oferta</span>
              cartera real ·{" "}
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold ml-1 mr-1">Mercado (QA)</span>
              scraper filtrado, off por defecto
            </div>
          </div>
        </aside>
      </section>

      {/* Renovaciones + Competidores */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ti-renewals">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1"><Timer size={14} className="text-orange-600"/> Renovaciones estimadas</h3>
            <div className="flex gap-1">
              {[
                { k: "30",  label: "≤30d",  cls: "bg-rose-100 text-rose-800" },
                { k: "60",  label: "30-60d", cls: "bg-orange-100 text-orange-800" },
                { k: "90",  label: "60-90d", cls: "bg-amber-100 text-amber-800" },
                { k: "180", label: "≤6m",   cls: "bg-yellow-100 text-yellow-800" },
                { k: "360", label: "≤1a",   cls: "bg-emerald-100 text-emerald-800" },
              ].map(b => (
                <button key={b.k} onClick={() => setBucketFilter(bucketFilter === b.k ? "" : b.k)} data-testid={`ti-bucket-${b.k}`}
                  className={`text-[10px] px-2 py-0.5 rounded ${bucketFilter === b.k ? b.cls + " ring-1 ring-black/10 font-bold" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                  {b.label}
                </button>
              ))}
              {bucketFilter && (
                <button onClick={() => setBucketFilter("")} className="text-[10px] text-slate-400 hover:text-rose-600">✕</button>
              )}
            </div>
          </div>
          <div className="max-h-96 overflow-auto">
            {filteredRenewals.length === 0 && <div className="p-6 text-center text-xs text-slate-500">Sin renovaciones para este filtro.</div>}
            {filteredRenewals.map(c => {
              const badgeCls = c.confidence === "alta" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700";
              const daysCls = c.dias_restantes < 0 ? "text-rose-700 font-bold"
                : c.dias_restantes <= 30 ? "text-rose-600 font-bold"
                : c.dias_restantes <= 90 ? "text-orange-600 font-semibold"
                : c.dias_restantes <= 180 ? "text-amber-600" : "text-slate-500";
              return (
                <div key={c.id} className="border-b border-slate-100 px-3 py-2 hover:bg-slate-50 transition" data-testid={`ti-renewal-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-slate-800 truncate">{c.cliente || "Cliente sin datos"}</div>
                      <div className="text-[11px] text-slate-500 truncate">{c.objeto || "—"}</div>
                      <div className="text-[10px] text-slate-400 flex flex-wrap gap-x-2 mt-0.5">
                        {c.expediente && <span>Exp: {c.expediente}</span>}
                        {c.winner && <span>Actual: <b>{c.winner}</b></span>}
                        {c.duracion_meses && <span>Dur: {c.duracion_meses}m</span>}
                        {c.presupuesto_mm != null && <span className="font-mono">{Number(c.presupuesto_mm).toFixed(1)}M</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-xs ${daysCls}`}>
                        {c.dias_restantes < 0 ? `Hace ${Math.abs(c.dias_restantes)}d` : `En ${c.dias_restantes}d`}
                      </div>
                      <div className="text-[9px] text-slate-400 font-mono">{fmtFechaOferta(c.proxima_licitacion)}</div>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${badgeCls}`}>{c.confidence}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ti-competitors">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 bg-slate-50">
            <Trophy size={14} className="text-rose-600"/>
            <h3 className="text-sm font-semibold text-slate-800">Top competidores</h3>
          </div>
          <div className="p-2 space-y-2 max-h-96 overflow-auto">
            {competitors.top.length === 0 && <div className="text-center text-xs text-slate-500 py-6">Sin datos de competidores.</div>}
            {competitors.top.map((c, i) => (
              <div key={i} className="border border-slate-100 rounded-lg p-2 hover:bg-slate-50 transition" data-testid={`ti-competitor-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-slate-800 truncate">
                    <span className="text-slate-400 mr-1 font-mono">#{i+1}</span>{c.name}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 font-mono">{c.cuota_pct}%</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 flex gap-2">
                  <span><b>{c.wins}</b> adjudicaciones</span>
                  <span className="font-mono">{fmtM(c.importe)}</span>
                </div>
                {c.clientes?.length > 0 && (
                  <div className="text-[10px] text-slate-400 mt-1 truncate">Clientes: {c.clientes.slice(0, 3).join(", ")}{c.clientes.length > 3 ? "…" : ""}</div>
                )}
                <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-rose-400 to-red-600" style={{width: `${c.cuota_pct}%`}}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </>)}
    </div>
  );
}

function KPICard({ label, value, icon, color, testid, onClick, active }) {
  const isBtn = !!onClick;
  const Cmp = isBtn ? "button" : "div";
  return (
    <Cmp
      onClick={onClick}
      data-testid={testid}
      className={`rounded-xl border p-3 shadow-sm text-left transition ${
        active ? "ring-2 ring-offset-1 ring-indigo-500 border-transparent" : "border-slate-200 bg-white"
      } ${isBtn ? "cursor-pointer hover:shadow-md" : ""}`}>
      <div className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full text-white bg-gradient-to-r ${color}`}>
        {icon} {label}
      </div>
      <div className="text-2xl font-bold text-slate-800 mt-1 font-mono">{value}</div>
    </Cmp>
  );
}
